import React, { useState } from 'react';
import {
  ActivityIndicator,
  Image, Pressable, StyleSheet, Text, View,
} from 'react-native';
import { BottomSheetBackdrop, BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet';
import EllipsisIcon from '../EllipsisIcon';
import Colors from '../../constants/Colors';
import DeleteModal from '../DeleteModal';
import { logEvent } from '../LogEvent';
import titleCase from '../TitleCase';
import EnvelopeIcon16 from '../16pxIcons/EnvelopeIcon16';
import { TAG_COLORS, UserStatus } from '../../constants/Types';
import reinviteUser from '../../requests/Users/ReinviteUser';
import AlertWidget from '../AlertWidget';
import EditUserMobile from './EditUserMobile';

interface UserCardMobileProps {
  user: {
    firstName: string,
    lastName: string,
    email: string,
    icon?: string,
    facilityManager?: boolean,
    lastAuthenticatedAt: string,
    isAdmin: boolean,
    roleList?: { id: string, name: string, color: string }[],
    status?: string,
    hideSectionDropdown: () => void,
    userGroupMemberships?: { userGroupId: string, name: string }[],
  },
  roles: object[],
  groupName?: string,
  deleteAction?: () => void,
  canUpdateUsers?: boolean,
  canDeleteUsers?: boolean,
  hideSectionDropdown: () => void,
  userGroups?: { Name: string, UserGroupId: string }[],
  selectedUserGroup?: string,
  updateUsers?: boolean,
  // eslint-disable-next-line no-unused-vars
  setUpdateUsers?: (update: boolean) => void,
}

export default function UserCardMobile(props: UserCardMobileProps) {
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [deleteUserModalVisible, setDeleteUserModalVisible] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [loading, setLoading] = useState(false);

  const fullName: string = `${props.user.firstName ? titleCase(props.user.firstName) : props.user.email} ${titleCase(props.user.lastName)}`;

  const bottomSheetMenuRef = React.useRef(null);

  const generateColor = (length) => {
    const randomColor = Math.floor((1 / length) * 16777215)
      .toString(16)
      .padStart(6, '0');
    return `#${randomColor}`;
  };

  const renderUserIcon = (imageSource, facilityManager = false) => {
    if (imageSource === undefined) {
      return (
        <View
          style={{
            width: 40,
            height: 40,
            borderRadius: 20,
            backgroundColor: generateColor(fullName.length),
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text
            style={{
              color: '#fff',
              fontFamily: 'Roboto_400Regular',
              fontSize: 14,
            }}
          >
            {props.user.firstName ? props.user.firstName[0]?.toUpperCase()
              : props.user.email[0]?.toUpperCase()}
            {props.user.lastName ? props.user.lastName[0]?.toUpperCase() : ''}
          </Text>
        </View>
      );
    }
    return (
      <View
        style={{
          width: 40,
          height: 40,
          borderRadius: 20,
          backgroundColor: facilityManager ? '#fff' : 'transparent',
          borderWidth: 1.5,
          borderColor: facilityManager ? Colors.light.hereworksBlue500 : 'transparent',
          justifyContent: 'center',
          alignItems: 'center',
          marginLeft: -14,
        }}
      >
        <Image
          style={styles.userPic}
          source={{
            uri: imageSource,
          }}
        />
      </View>
    );
  };

  return (
    <View style={{
      flex: 1,
      borderColor: Colors.light.hereworksWhite300,
      paddingVertical: 16,
      paddingLeft: 16,
      paddingRight: 12,
      backgroundColor: '#fff',
      borderWidth: 1,
      borderRadius: 8,
      marginVertical: 4,
    }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
        <View>
          {renderUserIcon(props.user.icon, props.user.facilityManager)}
        </View>
        <View>
          <Text style={styles.nameStyle}>{props.user.firstName?.trim() ? props.user.firstName
            : props.user.email} {props.user.lastName}</Text>
          <View style={{ }}>
            {(props.user.roleList?.length) ? <>
                {props.user.roleList.map((role, index) => (
                  <Pressable
                    key={role.id}
                    style={{
                      flexDirection: 'row',
                      alignSelf: 'flex-start',
                      backgroundColor: TAG_COLORS[role.color || 'blue' as keyof typeof TAG_COLORS].background,
                      paddingHorizontal: 8,
                      marginTop: 4,
                      marginLeft: 8,
                      borderRadius: 8,
                    }}>
                      <Text key={role.id} style={{
                        fontFamily: 'Poppins_400Regular',
                        fontSize: 12,
                        color: TAG_COLORS[role.color || 'blue' as keyof typeof TAG_COLORS].text,
                        lineHeight: 18,
                      }}>
                        {role.name}{index < props.user.roleList.length - 1 ? ', ' : ''}
                      </Text>
                    </Pressable>
                ))}</> : <Pressable>
              <Text style={[styles.userDetails, {
                alignSelf: 'flex-start',
                marginLeft: 8,
                fontFamily: 'Poppins_400Regular',
                fontSize: 12,
                lineHeight: 18,
                backgroundColor: Colors.light.hereworksBlue50,
                color: Colors.light.hereworksBlue600,
                paddingHorizontal: 8,
                marginTop: 4,
                borderRadius: 65,
              }]}>
                {props.user.isAdmin ? 'Admin' : 'Non Admin'}
              </Text></Pressable>}
          </View>
        </View>
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <EnvelopeIcon16 />
        <Text
          style={[
            styles.userDetails,
            {
              marginLeft: 8,
              flex: 1,
            },
          ]}
        >
          {props.user.email}
        </Text>
        {props.user.status === UserStatus.PENDING_VERIFICATION && <Text style={{
          marginLeft: 8,
          fontFamily: 'Poppins_400Regular',
          fontSize: 12,
          lineHeight: 18,
          backgroundColor: Colors.light.hereworksYellow50,
          color: Colors.light.hereworksYellow900,
          borderWidth: 1,
          borderColor: Colors.light.hereworksYellow900,
          paddingHorizontal: 8,
          borderRadius: 4,
        }}>
          Pending
        </Text>}
      </View>
      {(props.user.userGroupMemberships.length && !props.selectedUserGroup) ? <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          marginTop: 6,
          flexWrap: 'wrap',
        }}>
        {props.user.userGroupMemberships?.map((group) => (
          <View key={group.userGroupId}>
            <Text key={group.userGroupId} style={{
              marginRight: 4,
              fontFamily: 'Poppins_400Regular',
              fontSize: 12,
              lineHeight: 18,
              backgroundColor: Colors.light.hereworksDarkBlue,
              color: '#fff',
              paddingHorizontal: 8,
              paddingVertical: 2,
              borderRadius: 4,
              marginVertical: 2,
            }}>
              {
                props.userGroups?.find(
                  (userGroup: any) => userGroup.UserGroupId === group.userGroupId,
                )?.Name
              }
            </Text>
          </View>
        ))}
        </View> : null}
      <DeleteModal
        modalVisible={deleteUserModalVisible}
        setModalVisible={setDeleteUserModalVisible}
        name={fullName}
        groupName={props.groupName}
        deleteAction={() => props.deleteAction()}
        mobile={true}
        title={'Delete the user'}
      />
      {props.canUpdateUsers && (
        <Pressable
          style={{ position: 'absolute', top: 8, right: 8 }}
          onPress={() => {
            props.hideSectionDropdown();
            bottomSheetMenuRef.current?.present();
            logEvent('UserCardMobile', 'user_options_opened');
          }}
        >
          <EllipsisIcon fill={Colors.light.hereworksBlue400} />
        </Pressable>
      )}
      <BottomSheetModal
        ref={bottomSheetMenuRef}
        index={1}
        snapPoints={['30%']}
        backdropComponent={(backdropProps) => (<BottomSheetBackdrop
          {...backdropProps} disappearsOnIndex={-1} appearsOnIndex={0} />)}
      >
        <BottomSheetView>
          {!editModalVisible && !deleteUserModalVisible && <>
          <Text style={{
            textAlign: 'center',
            fontFamily: 'Poppins_500Medium',
            fontSize: 16,
            color: Colors.light.hereworksBlack600,
            marginBottom: 20,
          }}>{props.user.firstName?.trim() ? props.user.firstName
            : props.user.email} {props.user.lastName}</Text>
        {(!showAlert && props.canUpdateUsers)
          && <Pressable style={styles.contextItem} onPress={() => {
            setEditModalVisible(true);
            logEvent('UserCardMobile', 'edit_user_clicked');
          }}>
            <Text style={styles.contextItemText}>Edit user role</Text>
          </Pressable>}
          {(!showAlert && props.canDeleteUsers) && (
            <Pressable style={styles.contextItem} onPress={() => {
              setDeleteUserModalVisible(true);
              bottomSheetMenuRef.current?.dismiss();
              logEvent('UserCardMobile', 'delete_user_clicked');
            }}>
              <Text style={styles.contextItemText}>Delete</Text>
            </Pressable>
          )}
          {(!showAlert && props.user.status === UserStatus.PENDING_VERIFICATION) && (
            <Pressable style={styles.contextItem} onPress={() => {
              logEvent('UserCardMobile', 'resend_verification_email_clicked');
              setLoading(true);
              reinviteUser(props.user.email).then(() => {
                setShowAlert(true);
                setLoading(false);
              });
            }}>
              <Text style={styles.contextItemText}>Resend invitation email</Text>
            </Pressable>
          )}
          {loading && (
            <View style={[StyleSheet.absoluteFillObject, { justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(255, 255, 255, 0.6)' }]}>
              <ActivityIndicator size="small" color={Colors.light.hereworksBlue500} />
              <Text style={styles.contextItemText}>Sending...</Text>
            </View>
          )}
          {showAlert && (
            <AlertWidget
              title="Invitation Email Sent"
              message={`An invitation email has been resent to ${props.user.email}.`}
              error={false}
              cancelAction={() => {
                setShowAlert(false);
                bottomSheetMenuRef.current?.dismiss();
              }}
            />
          )}</>}
          {editModalVisible && <EditUserMobile
            user={props.user}
            editModalVisible={editModalVisible}
            setEditModalVisible={setEditModalVisible}
            roleChanged={() => {
              if (props.setUpdateUsers) {
                props.setUpdateUsers(!props.updateUsers);
              }
            }}
            roles={props.roles}
            mobile={true}
            onClose={() => bottomSheetMenuRef.current?.dismiss()}
          />}
        </BottomSheetView>
      </BottomSheetModal>
    </View>
  );
}

const styles = StyleSheet.create({
  userPic: {
    width: 40,
    height: 40,
    borderRadius: 20,
    overflow: 'hidden',
  },
  headerText: {
    fontFamily: 'Roboto_500Medium',
    fontSize: 11,
    lineHeight: 16.5,
    letterSpacing: 0.7,
    color: Colors.light.hereworksBlack400,
  },
  nameStyle: {
    marginLeft: 12,
    fontFamily: 'Poppins_500Medium',
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
    flexWrap: 'wrap',
    marginRight: 50,
  },
  userDetails: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    color: Colors.light.hereworksBlack600,
  },
  contextItem: {
    marginVertical: 9,
    backgroundColor: '#fff',
  },
  contextItemText: {
    marginLeft: 12,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    lineHeight: 21,
  },
});
